import React, { useState, useRef, type KeyboardEvent } from 'react';
import { InputText } from 'primereact/inputtext';
import { ReactComponent as DownArrowIcon } from '../../../assets/shared/ic-shared-down-arrow.svg';
import { ReactComponent as ClearIcon } from '../../../assets/shared/ic-shared-clear.svg';
import { SelectMenu } from './components/SelectMenu';
import {
  type MenuItem,
  type LiliComboboxWithInputValueConstraint,
} from './components/SelectMenuItem';
import { twMerge } from 'tailwind-merge';
import { useFontClass } from '../../tokens';

interface LiliComboboxWithInputProps<T extends LiliComboboxWithInputValueConstraint> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'type' | 'id'
> {
  id: string;
  items: MenuItem<T>[];
  selectedComboBoxValue: T;
  onItemSelected: (item: T) => void;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isSelectMenuDisabled?: boolean;
  selectSearchPlaceholder: string;
  SelectSearchPrefixIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onKeyDown?: (ev: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  dataTestId?: string;
  onFocus?: () => void;
  autoCompleteProperty?: string;
}

export const LiliComboBoxWithInput = <T extends LiliComboboxWithInputValueConstraint>({
  id,
  items,
  selectedComboBoxValue,
  onItemSelected,
  inputValue,
  onInputValueChange,
  placeholder,
  isDisabled,
  isSelectMenuDisabled,
  selectSearchPlaceholder,
  SelectSearchPrefixIcon,
  onKeyDown,
  onBlur,
  dataTestId,
  onFocus,
  autoCompleteProperty,
  ...otherProps
}: LiliComboboxWithInputProps<T>) => {
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const elTriggerButton = useRef<HTMLButtonElement | null>(null);
  const { fontClass, placeholderFontClass } = useFontClass();

  const selectedItem = items.find(({ value }) => value === selectedComboBoxValue) || items[0];

  const openSelectBtnClass = twMerge(
    'w-fit flex items-center justify-center flex-grow rounded-bl-[8px] rounded-tl-[8px] border-r border-outline-primary-bw-04 cursor-default',
    !isSelectMenuDisabled && !isDisabled && 'cursor-pointer hover:bg-surface-primary-bw-03',
    !isSelectMenuDisabled && !isDisabled && isSelectOpen && 'bg-surface-primary-bw-03'
  );

  // TODO: Find name
  const selectBtnInnterClass = twMerge(
    'py-[9px] pl-[16px] pr-[16px] flex items-center gap-[4px]',
    !isSelectMenuDisabled && !isDisabled && 'pr-[8px]'
  );

  const arrowIconClass = twMerge(
    'w-[24px] text-outline-primary-bw-06',
    isSelectOpen && 'rotate-180'
  );

  return (
    <div
      translate='no'
      className='w-full flex rounded-[8px] border border-outline-primary-bw-04 relative h-[54px]'
      {...otherProps}>
      <button
        ref={elTriggerButton}
        data-testid={`${id}-button-trigger`}
        className={openSelectBtnClass}
        onClick={(event) => {
          event.stopPropagation();
          if (isDisabled || isSelectMenuDisabled) return;
          setIsSelectOpen((prevState) => !prevState);
        }}
        type='button'>
        <div translate='no' className={selectBtnInnterClass}>
          <span className={`text-[38px] ${fontClass}`}>{selectedItem.title}</span>
          {!isDisabled && !isSelectMenuDisabled && <DownArrowIcon className={arrowIconClass} />}
        </div>
      </button>

      <div
        translate='no'
        className='w-full flex items-center gap-[4px] pl-[12px] pr-[16px] py-[14px] hover:bg-surface-primary-bw-00 rounded-br-[8px] rounded-tr-[8px]'>
        <InputText
          onFocus={onFocus}
          onBlur={onBlur}
          autoComplete={autoCompleteProperty}
          data-testid={dataTestId ?? id}
          id={id}
          onChange={(event) => onInputValueChange(event?.target?.value || '')}
          className={`w-full outline-none shadow-none bg-transparent ${fontClass} ${placeholderFontClass}`}
          unstyled
          value={inputValue}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
          disabled={isDisabled}
          data-hj-allow
        />
        {inputValue && (
          <button onClick={() => onInputValueChange('')} type='button'>
            <ClearIcon />
          </button>
        )}
      </div>
      {isSelectOpen && (
        <SelectMenu
          id={id}
          elTriggerButton={elTriggerButton}
          setIsSelectOpen={setIsSelectOpen}
          onItemSelected={(value) => {
            onItemSelected(value);
          }}
          selectedValue={selectedComboBoxValue}
          items={items}
          inputPlaceholder={selectSearchPlaceholder}
          InputPrefixIcon={SelectSearchPrefixIcon}
        />
      )}
    </div>
  );
};
